import React, { useContext, useState } from 'react';
import { useAsyncEffect } from '../../../hooks/UseAsyncEffect';
import { toCurrency, toFormattedDate } from '../../../utilities/FormatHelpers';
import { AccountContext } from '../AccountContext';
import { TransactionResult } from '../../../sdk/SearchServiceClient';
import ReleasePayment from './ReleasePayment';

export default function UnconfirmedPayments(props) {
  const { accountId, searchService, setError } = useContext(AccountContext);
  const [loading, setLoading] = useState(true as boolean);
  const [refresh, setRefresh] = useState('');
  const [payments, setPayments] = useState<TransactionResult[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const unConfirmedPayments = payments.filter(
    (payment) => payment.creditRelease === 'CreditReleaseDelayed' && payment.isCreditReleased !== true
  );

  const { paymentCreated } = props;

  const doRefresh = (refresh) => {
    setRefresh(refresh);
  };

  useAsyncEffect(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await searchService.getAccountPayments(accountId);
      setPayments(response);
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : String(error);
      setLoadError(message);
      setPayments([]);
      setError({ source: 'Get Unconfirmed Payments', message });
    }
    finally{
      setLoading(false);
    }
  }, [accountId, refresh, paymentCreated]);

  return (
    <>
      <h2>Unconfirmed Payments</h2>
      {loading && <div className="ui active centered inline loader" />}
      {!loading && loadError && <p role="alert">Could not load payments: {loadError}</p>}

      {!loading && !loadError && (unConfirmedPayments == null || !unConfirmedPayments.length) && <p>No Unconfirmed Payments</p>}

      {!loading && !loadError && unConfirmedPayments && unConfirmedPayments.length > 0 && (
        <div className="scrollable">
          <table className="ui single line table">
            <thead>
              <tr>
                <th>Transaction</th>
                <th>Amount</th>
                <th>Effective Date</th>
                <th>Payment Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {unConfirmedPayments.map((a) => (
                <React.Fragment key={a.transactionId}>
                  <tr>
                    <td>
                      {a.description}
                      <div className="ui grey sub header">{a.transactionId}</div>
                    </td>
                    <td>{toCurrency(a.amount.value)}</td>
                    <td>{toFormattedDate(a.effectiveDate, 'yyyy-MM-dd HH:mm:ss')}</td>
                    <td>{a.transactionDate ? toFormattedDate(a.transactionDate, 'yyyy-MM-dd HH:mm:ss') : 'N/A'}</td>
                    <td className="collapsing">
                      <div className="ui actions">
                        <ReleasePayment
                          key={`release-payment-${a.transactionId}`}
                          transactionId={a.transactionId}
                          setRefresh={doRefresh}
                        />
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h2>All Payments</h2>
      {!loading && !loadError && payments && payments.length > 0 && (
        <div className="scrollable">
          <table className="ui single line table">
            <thead>
              <tr>
                <th>Transaction</th>
                <th>Amount</th>
                <th>Effective Date</th>
                <th>Payment Date</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((a) => (
                <React.Fragment key={a.transactionId}>
                  <tr>
                    <td>
                      {a.description}
                      <div className="ui grey sub header">{a.transactionId}</div>
                    </td>
                    <td>{toCurrency(a.amount.value)}</td>
                    <td>{toFormattedDate(a.effectiveDate, 'yyyy-MM-dd HH:mm:ss')}</td>
                    <td>{a.transactionDate ? toFormattedDate(a.transactionDate, 'yyyy-MM-dd HH:mm:ss') : 'N/A'}</td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && !loadError && payments && payments.length == 0 && <p>No Payments</p>}
    </>
  );
}
